import { useCallback, useEffect, useState } from 'react';
import { EngineError, getDelinquencyReport, getSignedAgreementsReport, getWaterfallReport, type DelinquencyReport, type SignedAgreementsReport, type WaterfallReport } from '../../api/engineDashboard';

/**
 * Live delinquency wave report (Danny, 2026-09-15: "turn this into a
 * real-time report so we can all just see it") — the Nomad briefing
 * page, computed fresh from the engine on every load and refreshed
 * every 60 seconds. Org-scoped: shows whichever delinquency sources
 * the signed-in account can see.
 */

interface Props {
  token: string;
  onUnauthorized: () => void;
}

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export function ReportsPage({ token, onUnauthorized }: Props) {
  const tokenForAgreements = token;
  const onUnauthorizedForAgreements = onUnauthorized;
  const [report, setReport] = useState<DelinquencyReport | null>(null);
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const load = useCallback(async (selected?: string) => {
    try {
      const res = await getDelinquencyReport(token, selected);
      if (!('totals' in res)) {
        setReport(null);
        setError('No delinquency program in your scope yet.');
        return;
      }
      setReport(res);
      setOrgId(res.org_id);
      setUpdatedAt(new Date());
      setError(null);
    } catch (err) {
      if (err instanceof EngineError && err.status === 401) return onUnauthorized();
      setError(err instanceof EngineError ? err.message : 'Could not load the report.');
    }
  }, [token, onUnauthorized]);

  useEffect(() => {
    void load(orgId);
    const t = setInterval(() => void load(orgId), 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, orgId]);

  if (!report) {
    return (
      <div className="py-16 text-center text-sm text-gray-500">
        {error ?? 'Loading the live report…'}
      </div>
    );
  }

  const t = report.totals;
  const orgName = report.orgs.find((o) => o.id === report.org_id)?.name ?? 'Delinquency program';
  const funnel: Array<[string, string, string]> = [
    [String(t.cases), 'Cases opened', 'Imported & assessed from the daily file'],
    [String(t.contacted), 'Tenants contacted', 'SMS + email, contact-hours aware'],
    [String(t.intakes), 'Intakes completed', 'Self-serve, no staff time'],
    [String(t.accepted), 'Offers accepted', `Plus ${t.proposals} counter-proposals in negotiation`],
    [money(t.committed_usd), 'Committed', `${t.resolved} cases fully resolved`],
  ];

  return (
    <div className="mx-auto max-w-5xl">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-xl font-semibold">{orgName} — live report</h1>
        {report.orgs.length > 1 && (
          <select
            className="rounded border border-surface-border px-2 py-1 text-sm"
            value={report.org_id}
            onChange={(e) => void load(e.target.value)}
          >
            {report.orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        )}
        <span className="ml-auto text-xs text-gray-400">
          Live · updated {updatedAt ? updatedAt.toLocaleTimeString() : '—'} · refreshes every minute
        </span>
      </div>

      {error && <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {/* funnel */}
      <div className="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-surface-border bg-surface-border sm:grid-cols-5">
        {funnel.map(([n, label, d], i) => (
          <div key={label} className={`p-4 ${i === 4 ? 'bg-green-50' : 'bg-white'}`}>
            <div className={`text-2xl font-bold tabular-nums ${i === 4 ? 'text-green-700' : ''}`}>{n}</div>
            <div className="mt-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
            <div className="mt-0.5 text-xs text-gray-500">{d}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {/* resolved */}
        <div className="card p-4">
          <div className="section-label">Resolved — {t.resolved} cases · {money(t.committed_usd)}</div>
          <table className="mt-2 w-full text-sm">
            <tbody>
              {report.resolved_cases.map((r, i) => (
                <tr key={i} className="border-t border-surface-border">
                  <td className="py-1.5 text-gray-500">{new Date(r.at).toLocaleDateString()}</td>
                  <td className="py-1.5">{r.kind}</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums text-green-700">{money(r.amount)}</td>
                </tr>
              ))}
              {report.resolved_cases.length === 0 && (
                <tr><td className="py-2 text-xs text-gray-400">No resolutions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* engagement + program */}
        <div className="space-y-4">
          <div className="card p-4">
            <div className="section-label">Engagement</div>
            <table className="mt-2 w-full text-sm">
              <tbody>
                {(
                  [
                    ['Signed Agreement to Mediate', t.a2m],
                    ['Counter-proposals made', t.proposals],
                    ['Messages from tenants', t.inbound],
                    ['Escalating toward mediation', t.escalating],
                    ['Mediations held', t.sessions_held],
                    ['Mediations scheduled', t.sessions_upcoming],
                  ] as Array<[string, number]>
                ).map(([label, n]) => (
                  <tr key={label} className="border-t border-surface-border">
                    <td className="py-1.5">{label}</td>
                    <td className="py-1.5 text-right font-semibold tabular-nums">{n}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card p-4">
            <div className="section-label">Working quietly</div>
            <table className="mt-2 w-full text-sm">
              <tbody>
                <tr className="border-t border-surface-border">
                  <td className="py-1.5">Resolved externally, auto-closed by the daily sync</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">{t.resolved_external}</td>
                </tr>
                <tr className="border-t border-surface-border">
                  <td className="py-1.5">Engaged tenants paused for off-file review</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">{t.off_file_review}</td>
                </tr>
                <tr className="border-t border-surface-border">
                  <td className="py-1.5">Messages delivered / failed</td>
                  <td className="py-1.5 text-right font-semibold tabular-nums">
                    {report.delivery.delivered} / {report.delivery.failed}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* cohorts */}
      <div className="card mt-4 overflow-x-auto p-4">
        <div className="section-label">By import cohort</div>
        <table className="mt-2 w-full min-w-[640px] text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
              <th className="py-1 font-medium">Cohort</th>
              <th className="py-1 text-right font-medium">Cases</th>
              <th className="py-1 text-right font-medium">Contacted</th>
              <th className="py-1 text-right font-medium">Intakes</th>
              <th className="py-1 text-right font-medium">Accepted</th>
              <th className="py-1 text-right font-medium">Resolved</th>
              <th className="py-1 text-right font-medium">Committed</th>
            </tr>
          </thead>
          <tbody>
            {report.cohorts.map((cRow) => (
              <tr key={cRow.cohort} className="border-t border-surface-border">
                <td className="py-1.5 font-medium">{cRow.cohort}</td>
                <td className="py-1.5 text-right tabular-nums">{cRow.cases}</td>
                <td className="py-1.5 text-right tabular-nums">{cRow.contacted}</td>
                <td className="py-1.5 text-right tabular-nums">{cRow.intakes}</td>
                <td className="py-1.5 text-right tabular-nums">{cRow.accepted}</td>
                <td className="py-1.5 text-right tabular-nums">{cRow.resolved}</td>
                <td className="py-1.5 text-right font-semibold tabular-nums text-green-700">{money(cRow.committed_usd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-400">
        Payment commitments are documented in-platform and paid directly to the property manager — Levelheaded never
        touches the funds. Numbers computed live from the engine; test-tagged cases excluded.
      </p>

      <AgreementsReport token={tokenForAgreements} onUnauthorized={onUnauthorizedForAgreements} />
      <WaterfallSection token={tokenForAgreements} onUnauthorized={onUnauthorizedForAgreements} />
    </div>
  );
}

/* ═══════════ Signed agreements (the monthly "agreements" report) ═══════════ */

function monthWindow(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

function AgreementsReport({ token, onUnauthorized }: Props) {
  const [data, setData] = useState<SignedAgreementsReport | null>(null);
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const { from, to } = monthWindow(month);
    getSignedAgreementsReport(token, { org_id: orgId, from, to })
      .then((res) => { setData(res); if (res.report) setOrgId(res.report.org_id); setErr(null); })
      .catch((e) => {
        if (e instanceof EngineError && e.status === 401) return onUnauthorized();
        setErr(e instanceof Error ? e.message : 'Could not load agreements');
      });
  }, [token, orgId, month, onUnauthorized]);

  if (!data?.report) {
    return <div className="mt-10 text-sm text-gray-400">{err ?? 'Loading signed agreements…'}</div>;
  }
  const r = data.report;
  const monthLabel = new Date(`${month}-02T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  return (
    <div className="mt-12 border-t border-surface-border pt-8">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-lg font-semibold">Signed agreements — {monthLabel}</h2>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="rounded border border-surface-border px-2 py-1 text-sm" />
        {data.orgs.length > 1 && (
          <select className="rounded border border-surface-border px-2 py-1 text-sm" value={r.org_id}
            onChange={(e) => setOrgId(e.target.value)}>
            {data.orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-4">
        <div className="card p-3"><div className="text-xs text-gray-500">Signed agreements</div><div className="text-2xl font-bold tabular-nums">{r.kpis.signed}</div></div>
        <div className="card p-3"><div className="text-xs text-gray-500">Settled value (parameterized)</div><div className="text-2xl font-bold tabular-nums">{money(r.kpis.settled_value_usd)}</div><div className="text-[11px] text-gray-400">{r.kpis.settled_parameterized} of {r.kpis.signed} plans</div></div>
        <div className="card p-3"><div className="text-xs text-gray-500">Cases resolved this month</div><div className="text-2xl font-bold tabular-nums">{r.kpis.resolved_cases}</div><div className="text-[11px] text-gray-400">incl. off-platform closes</div></div>
        <div className="card p-3"><div className="text-xs text-gray-500">Plan types</div>
          {Object.entries(r.plan_types).map(([k, v]) => <div key={k} className="text-xs"><b>{v}</b> {k}</div>)}
          {r.kpis.signed === 0 && <div className="text-xs text-gray-400">—</div>}
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-surface-border text-left text-[11px] uppercase tracking-wide text-gray-500">
              <th className="py-2 pr-3">Signed</th><th className="py-2 pr-3">Tenant</th><th className="py-2 pr-3">Plan</th>
              <th className="py-2 pr-3 text-right">Terms</th><th className="py-2 pr-3 text-right">Plan total</th>
              <th className="py-2 pr-3">Payoff by</th><th className="py-2 pr-3">Mediator</th><th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {r.rows.map((row) => (
              <tr key={row.agreement_id} className="border-b border-surface-border/60">
                <td className="py-2 pr-3 tabular-nums">{row.signed_at}</td>
                <td className="py-2 pr-3 font-medium">{row.tenant}</td>
                <td className="py-2 pr-3"><span className="rounded-full bg-brand-subtle px-2 py-0.5 text-[11px] font-medium text-brand">{row.plan_type ?? row.doc_type.replace('_', ' ')}</span></td>
                <td className="py-2 pr-3 text-right tabular-nums">{row.terms_label ?? (row.plan_total == null ? 'custom / see agreement' : '—')}</td>
                <td className="py-2 pr-3 text-right font-semibold tabular-nums">{row.plan_total != null ? money(row.plan_total) : '—'}</td>
                <td className="py-2 pr-3 tabular-nums text-gray-500">{row.payoff_by ?? '—'}</td>
                <td className="py-2 pr-3 text-gray-500">{row.mediator ?? 'unassigned'}</td>
                <td className="py-2 text-right"><a className="text-xs font-medium text-brand underline" href={`/?open_case=${row.case_id}`}>open ↗</a></td>
              </tr>
            ))}
            {r.rows.length === 0 && <tr><td colSpan={8} className="py-4 text-sm text-gray-400">No signed agreements in this window.</td></tr>}
          </tbody>
          {r.rows.length > 0 && (
            <tfoot><tr className="border-t border-surface-border font-semibold">
              <td colSpan={4} className="py-2 pr-3">Total settled value (parameterized)</td>
              <td className="py-2 pr-3 text-right tabular-nums">{money(r.kpis.settled_value_usd)}</td>
              <td colSpan={3} />
            </tr></tfoot>
          )}
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-400">
        “Plan total” is monthly payment × number of payments for installment plans, or the pay-in-full amount; free-form
        terms show “custom / see agreement” (open the case for the full language). “Unassigned” means the case ran
        through the automated outreach ladder rather than a named mediator. Most delinquency resolution happens
        off-platform (tenants pay the landlord directly) — those show in “cases resolved” but not as signed agreements.
      </p>
    </div>
  );
}

/* ═══════════ Waterfall (Frank, 2026-11-03): period-batched funnel ═══════════ */

type PeriodKind = 'month' | 'quarter' | 'year';

function periodWindow(kind: PeriodKind, month: string, quarter: string, year: string): { from: string; to: string; label: string } {
  if (kind === 'month') {
    const [y, m] = month.split('-').map(Number);
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}`, label: new Date(`${month}-02T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }) };
  }
  if (kind === 'quarter') {
    const q = Number(quarter.slice(1));
    const y = Number(year);
    const sm = (q - 1) * 3 + 1;
    const last = new Date(Date.UTC(y, sm + 2, 0)).getUTCDate();
    return { from: `${y}-${String(sm).padStart(2, '0')}-01`, to: `${y}-${String(sm + 2).padStart(2, '0')}-${String(last).padStart(2, '0')}`, label: `${quarter} ${y}` };
  }
  return { from: `${year}-01-01`, to: `${year}-12-31`, label: year };
}

function WaterfallSection({ token, onUnauthorized }: Props) {
  const now = new Date();
  const [kind, setKind] = useState<PeriodKind>('month');
  const [month, setMonth] = useState(() => now.toISOString().slice(0, 7));
  const [quarter, setQuarter] = useState(() => `Q${Math.floor(now.getUTCMonth() / 3) + 1}`);
  const [year, setYear] = useState(() => String(now.getUTCFullYear()));
  const [orgId, setOrgId] = useState<string | undefined>(undefined);
  const [data, setData] = useState<WaterfallReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const win = periodWindow(kind, month, quarter, year);
  useEffect(() => {
    getWaterfallReport(token, { org_id: orgId, from: win.from, to: win.to })
      .then(setData)
      .catch((err: unknown) => {
        if (err instanceof EngineError && err.status === 401) return onUnauthorized();
        setError('Could not load the waterfall report.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, orgId, win.from, win.to, onUnauthorized]);

  const steps = data?.report?.steps ?? [];
  const max = Math.max(1, ...steps.map((st) => st.count));
  const years = Array.from({ length: 3 }, (_, i) => String(now.getUTCFullYear() - i));
  return (
    <section className="card mt-6 space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">Waterfall — {win.label}</h2>
        <select value={kind} onChange={(e) => setKind(e.target.value as PeriodKind)} className="rounded-md border border-surface-border px-2 py-1 text-sm">
          <option value="month">By month</option>
          <option value="quarter">By quarter</option>
          <option value="year">By year</option>
        </select>
        {kind === 'month' && (
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-md border border-surface-border px-2 py-1 text-sm" />
        )}
        {kind === 'quarter' && (
          <>
            <select value={quarter} onChange={(e) => setQuarter(e.target.value)} className="rounded-md border border-surface-border px-2 py-1 text-sm">
              {['Q1', 'Q2', 'Q3', 'Q4'].map((q) => <option key={q} value={q}>{q}</option>)}
            </select>
            <select value={year} onChange={(e) => setYear(e.target.value)} className="rounded-md border border-surface-border px-2 py-1 text-sm">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </>
        )}
        {kind === 'year' && (
          <select value={year} onChange={(e) => setYear(e.target.value)} className="rounded-md border border-surface-border px-2 py-1 text-sm">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        {data && data.orgs.length > 1 && (
          <select value={data.org_id} onChange={(e) => setOrgId(e.target.value)} className="ml-auto rounded-md border border-surface-border px-2 py-1 text-sm">
            {data.orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        )}
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}
      {!data?.report && !error && <div className="text-sm text-gray-400">Loading…</div>}
      {data?.report && (
        <div className="space-y-1.5">
          {steps.map((st) => (
            <div key={st.key} className="flex items-center gap-3">
              <div className="w-72 shrink-0 text-sm text-gray-700">{st.label}</div>
              <div className="h-5 flex-1 rounded bg-surface-muted">
                <div className="h-5 rounded bg-brand/70" style={{ width: `${(st.count / max) * 100}%` }} />
              </div>
              <div className="w-12 text-right text-sm font-semibold tabular-nums">{st.count}</div>
            </div>
          ))}
          <p className="pt-2 text-[11px] text-gray-400">
            Every count is events inside the selected period (cases opened, closes, signings, bookings), so periods batch cleanly and sum across months. A case opened in one month can resolve in the next.
          </p>
        </div>
      )}
    </section>
  );
}